import React from 'react';
import PropTypes from 'prop-types';
import { renderFileList } from '../../shared/content/renderFileList';

export const evidenceRequestAdditionalInfo = (
  <va-additional-info trigger="The differences between VA and private medical centers">
    <p>
      <strong>VA medical centers</strong> include VA medical centers or clinics,
      as well as doctors through the TRICARE health care program.
    </p>
    <p>
      <strong>Private medical centers</strong> include private doctors, such as
      Veterans Choice doctor. You can also choose to upload copies of your
      medical records instead of giving us permission to request them.
    </p>
  </va-additional-info>
);

// Alternative to evidenceRequestAdditionalInfo.
// This is the "B" experience of the A/B study for the conditions evidence messaging test (~Sep 2026)
export const medicalEvidencePactAdditionalInfo = (
  <va-additional-info trigger="Medical evidence for PACT Act claims">
    <p>
      If you’re claiming a condition covered by the PACT Act, provide medical
      evidence that you have the condition.
    </p>
  </va-additional-info>
);

// Note appended to the destructive modal.
// This is the "B" experience of the A/B study for the conditions evidence messaging test (~Sep 2026)
export const modalRemovalEvidenceNote = (
  <p>
    <strong>Note:</strong> Before removing, make sure your other evidence still
    covers all the conditions you’ve claimed.
  </p>
);

export const evidenceRequestQuestion = {
  label:
    'Are there medical records related to your claim that you’d like us to access on your behalf from VA or private medical centers?',
  hint: 'If you select “Yes,” we’ll request these records from VA or private medical centers. Or you can upload copies of your private medical records.',
};

export const vaEvidenceContent = (
  <p>
    You can choose not to submit medical records to support your claim. If you
    do so, we’ll remove the information you shared about these VA medical
    centers:
  </p>
);

export const privateEvidenceContent = (
  <p>
    You can choose not to submit medical records to support your claim. If you
    do so, we’ll delete these medical records you uploaded:
  </p>
);

export const privateEvidenceContentCombined = (
  <p>We’ll also delete these medical records you uploaded:</p>
);

export const privateFacilityContent = (
  <p>
    You can choose not to submit medical records to support your claim. If you
    do so, we’ll remove the information you shared about these private medical
    centers:
  </p>
);

export const privateFacilityContentCombined = (
  <p>
    We’ll also remove the information you shared about these private medical
    centers:
  </p>
);

export const alertMessageForCentersAndFiles =
  'We’ve removed information about your medical centers and deleted the medical records you uploaded from this claim.';

export const alertMessageForCenters =
  'We’ve removed information about your medical centers from this claim.';

export const alertMessageForFiles =
  'We’ve deleted the medical records you uploaded from this claim.';

export const alertMessage = alertType => {
  if (
    alertType.includes('privateMedicalRecords') &&
    (alertType.includes('va') || alertType.includes('privateFacility'))
  ) {
    return alertMessageForCentersAndFiles;
  }
  if (
    (alertType.includes('va') || alertType.includes('privateFacility')) &&
    !alertType.includes('privateMedicalRecords')
  ) {
    return alertMessageForCenters;
  }
  if (
    alertType.includes('privateMedicalRecords') &&
    !alertType.includes('va') &&
    !alertType.includes('privateFacility')
  ) {
    return alertMessageForFiles;
  }
  return '';
};
const maxDisplayedItems = 3;
export const renderFacilityList = (facilities, nameKey) => {
  const showAll = facilities.length <= maxDisplayedItems + 1;
  const displayList = showAll
    ? facilities
    : facilities.slice(0, maxDisplayedItems);
  return (
    <ul>
      {displayList.map((facility, index) => (
        <li key={index}>
          {facility[nameKey] || 'Name of medical center wasn’t added'}
        </li>
      ))}
      {!showAll && (
        <li>{facilities.length - maxDisplayedItems} other medical centers</li>
      )}
    </ul>
  );
};

export { renderFileList };

export const missingSelectionErrorMessageEvidenceRequestPage =
  'You must provide a response';

export const missingSelectionErrorMessageMedicalRecordPage =
  'Select at least one type of medical record';

export const medicalRecordQuestion =
  'What types of medical records would you like us to access on your behalf?';

export const vaEvidenceContentForMedicalRecordsPage = (
  <p>
    You can change the type of medical records you’d like us to access. If you
    do so, we’ll remove information you entered about these medical centers:
  </p>
);
export const privateEvidenceContentForMedicalRecordsPage = (
  <p>
    You can change the type of medical records you’d like us to access. If you
    do so, we’ll delete these medical records you uploaded:
  </p>
);
export const privateFacilityContentForMedicalRecordsPage = (
  <p>
    You can change the type of medical records you’d like us to access. If you
    do so, we’ll remove the information you shared about these private medical
    centers:
  </p>
);
export const ConfirmationEvidenceRequest = ({ formData }) => {
  const hasMedicalRecords = formData?.['view:hasMedicalRecords'];

  return (
    <li>
      <h4>Medical records that support your disability claim</h4>
      <div className="vads-u-padding--0">
        <div>
          <div className="vads-u-color--gray">
            {evidenceRequestQuestion.label}
          </div>
          <div>{hasMedicalRecords ? 'Yes' : 'No'}</div>
        </div>
      </div>
    </li>
  );
};

ConfirmationEvidenceRequest.propTypes = {
  formData: PropTypes.shape({
    'view:hasMedicalRecords': PropTypes.bool,
  }),
};

const confirmationMedicalRecordsOptions = 'Option(s) selected';

export const ConfirmationMedicalRecords = ({ formData }) => {
  const evidenceTypes = formData?.['view:selectableEvidenceTypes'] || {};
  const hasVa = evidenceTypes['view:hasVaMedicalRecords'];
  const hasPrivate = evidenceTypes['view:hasPrivateMedicalRecords'];

  const selections = [
    hasVa && 'VA medical records',
    hasPrivate && 'Private medical records',
  ].filter(Boolean);

  return (
    <li>
      <h4>Types of medical records</h4>
      <div className="vads-u-padding--0">
        <div className="vads-u-color--gray">
          {confirmationMedicalRecordsOptions}
        </div>
        <div>
          {selections.map(selection => (
            <div key={selection}>{selection}</div>
          ))}
        </div>
      </div>
    </li>
  );
};

ConfirmationMedicalRecords.propTypes = {
  formData: PropTypes.shape({
    'view:selectableEvidenceTypes': PropTypes.shape({
      'view:hasVaMedicalRecords': PropTypes.bool,
      'view:hasPrivateMedicalRecords': PropTypes.bool,
    }),
  }),
};
